import React, { useEffect, useLayoutEffect, useState, useRef, useCallback, useMemo } from 'react';
import Box from '@mui/joy/Box';
import Sheet from '@mui/joy/Sheet';
import Alert from '@mui/joy/Alert';
import Snackbar from '@mui/joy/Snackbar';
import Typography from '@mui/joy/Typography';
import Button from '@mui/joy/Button';
import Input from '@mui/joy/Input';
import IconButton from '@mui/joy/IconButton';
import Tabs from '@mui/joy/Tabs';
import TabList from '@mui/joy/TabList';
import Tab from '@mui/joy/Tab';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SendIcon from '@mui/icons-material/Send';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import ConversationList, { Conversation } from '../../components/chat/ConversationList';
import ChatHeader, { WhatsappLinkStatus } from '../../components/chat/ChatHeader';
import MessageBubble from '../../components/chat/MessageBubble';
import ComposerBar from '../../components/chat/ComposerBar';
import OrderSummaryPanel from '../../components/chat/OrderSummaryPanel';
import SupplierProductImportModal from '../../components/chat/SupplierProductImportModal';
import { useChat, ChatSendResult, ChatChannelProvider, WhatsappWebConnectionPayload } from '../../hooks/useChat';
import { useAuth } from '../../auth/AuthContext';
import { whatsappApi, WhatsAppStatus } from '../../api/whatsapp.api';
import { API_BASE_URL } from '../../api/config';
import { authHeadersForGet } from '../../api/http';
import { clientsApi } from '../../api/clients.api';
import { ClientType } from '../../types/client';

const WHATSAPP_POLL_MS = 30_000;
const MESSAGE_PAGE_SIZE = 50;
const NEAR_BOTTOM_PX = 80;
const DEFAULT_TEMPLATE_NAME = 'hello_world';
const DEFAULT_TEMPLATE_LANG = 'en_US';

function startOfLocalDay(value: Date): number {
  const date = new Date(value);
  return new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
}

function formatDaySeparator(value: Date): string {
  const date = new Date(value);
  const today = startOfLocalDay(new Date());
  const day = startOfLocalDay(date);
  const diffDays = Math.round((today - day) / 86400000);
  if (diffDays === 0) {
    return 'Hoy';
  }
  if (diffDays === 1) {
    return 'Ayer';
  }
  return date.toLocaleDateString('es-AR', { day: 'numeric', month: 'short', year: 'numeric' });
}

function applyWebLinkStatus(
  payload: WhatsappWebConnectionPayload,
): WhatsappLinkStatus | null {
  if (payload.phase === 'ready' || payload.phase === 'authenticated') {
    return 'connected';
  }
  if (payload.phase === 'qr' || payload.credential?.status === 'connecting') {
    return 'connecting';
  }
  if (payload.phase === 'disconnected' || payload.phase === 'error') {
    return 'disconnected';
  }
  return null;
}

function mapWebLinkStatus(status: string | undefined, hasCredential: boolean): WhatsappLinkStatus {
  if (!hasCredential || status === 'disconnected' || status === 'error') {
    return 'disconnected';
  }
  if (status === 'connecting') {
    return 'connecting';
  }
  return 'connected';
}

const ChatPage: React.FC = () => {
  const { companyId } = useAuth();
  const [channelTab, setChannelTab] = useState<ChatChannelProvider>('meta');
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedClientReference, setSelectedClientReference] = useState<string | null>(null);
  const [isLoadingConversations, setIsLoadingConversations] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [waStatus, setWaStatus] = useState<WhatsAppStatus | null>(null);
  const [waCheckError, setWaCheckError] = useState(false);
  const [webLinkStatus, setWebLinkStatus] = useState<WhatsappLinkStatus | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messageScrollRef = useRef<HTMLDivElement>(null);
  const isNearBottomRef = useRef(true);
  const pendingPrependRef = useRef(false);
  const previousScrollHeightRef = useRef(0);
  const lastSeenMessageIdRef = useRef<string | null>(null);
  const [hasUnreadBelow, setHasUnreadBelow] = useState(false);

  // ── Vista activa: resumen del pedido | conversación completa ──────────────
  const [chatView, setChatView] = useState<'summary' | 'chat'>('summary');
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedMessageReferences, setSelectedMessageReferences] = useState<string[]>([]);
  const [importModalOpen, setImportModalOpen] = useState(false);

  const refreshWhatsappStatus = useCallback(async () => {
    try {
      const data = await whatsappApi.getStatus();
      setWaStatus(data);
      setWaCheckError(false);
    } catch {
      setWaCheckError(true);
    }
  }, []);

  const whatsappOperational =
    !waCheckError &&
    (waStatus === null || Boolean(waStatus.whatsappAvailable ?? waStatus.isReady));

  const showWhatsappBanner =
    channelTab === 'meta' &&
    (waCheckError ||
      (waStatus !== null && !(waStatus.whatsappAvailable ?? waStatus.isReady)));

  const [templateName, setTemplateName] = useState(DEFAULT_TEMPLATE_NAME);
  const [templateLang, setTemplateLang] = useState(DEFAULT_TEMPLATE_LANG);
  const [isSendingTemplate, setIsSendingTemplate] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);

  const applySendResult = async (
    result: ChatSendResult,
    refreshOnFail: boolean,
  ): Promise<void> => {
    if (result.success) {
      setSendError(null);
      return;
    }
    setSendError(result.error ?? 'No se pudo enviar el mensaje. Intentá de nuevo.');
    if (refreshOnFail) {
      await refreshWhatsappStatus();
    }
  };

  const {
    messages,
    isConnected,
    isLoading,
    failedEvent,
    conversationUpdateTrigger,
    messagesPage,
    hasMoreMessages,
    webConnection,
    loadMessages,
    sendText,
    sendImage,
    sendAudio,
    sendLocation,
    sendSticker,
    sendTemplate,
    dismissFailedEvent,
  } = useChat(selectedClientReference, companyId ?? 0, channelTab);

  const conversationList = Array.isArray(conversations) ? conversations : [];

  const fetchConversations = useCallback(async (silent: boolean = false) => {
    try {
      if (!silent) setIsLoadingConversations(true);
      const providerParam = channelTab === 'whatsapp_web' ? 'whatsapp_web' : 'meta';
      const response = await fetch(
        `${API_BASE_URL}/chat/conversations?provider=${providerParam}`,
        { headers: authHeadersForGet() },
      );
      const data = await response.json();
      const list: Conversation[] = Array.isArray(data) ? data : [];
      setConversations(list);
      setSelectedClientReference((prev) => {
        if (!list.length) return null;
        if (prev !== null && list.some((c) => c.clientReference === prev)) return prev;
        return list[0].clientReference;
      });
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      if (!silent) setIsLoadingConversations(false);
    }
  }, [channelTab]);

  const scrollToBottom = (): void => {
    const container = messageScrollRef.current;
    if (!container) {
      return;
    }
    container.scrollTop = container.scrollHeight;
  };

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  useEffect(() => {
    refreshWhatsappStatus();
    const interval = setInterval(refreshWhatsappStatus, WHATSAPP_POLL_MS);
    return () => clearInterval(interval);
  }, [refreshWhatsappStatus]);

  const refreshWebLinkStatus = useCallback(async () => {
    try {
      const state = await whatsappApi.getWebState();
      setWebLinkStatus(mapWebLinkStatus(state.credential?.status, state.credential != null));
    } catch {
      setWebLinkStatus('disconnected');
    }
  }, []);

  useEffect(() => {
    void refreshWebLinkStatus();
  }, [refreshWebLinkStatus]);

  useEffect(() => {
    if (!webConnection) {
      return;
    }
    const nextStatus = applyWebLinkStatus(webConnection);
    if (nextStatus) {
      setWebLinkStatus(nextStatus);
    }
  }, [webConnection]);

  useEffect(() => {
    if (conversationUpdateTrigger > 0) {
      fetchConversations(true);
    }
  }, [conversationUpdateTrigger, fetchConversations]);

  useEffect(() => {
    if (selectedClientReference) {
      loadMessages(1, 50);
    }
  }, [selectedClientReference, loadMessages]);

  // ── Handlers de envío ─────────────────────────────────────────────────────

  const handleSendText = async (text: string) => {
    if (channelTab === 'whatsapp_web') return;
    if (!selectedClientReference || !conversationList.length || !whatsappOperational) return;
    try {
      setIsSending(true);
      const conversation = conversationList.find((c) => c.clientReference === selectedClientReference);
      if (conversation) {
        const result = await sendText(conversation.phoneNumber, text);
        await applySendResult(result, true);
      }
    } finally {
      setIsSending(false);
    }
  };

  const handleSendImage = async (file: File, caption?: string) => {
    if (channelTab === 'whatsapp_web') return;
    if (!selectedClientReference || !conversationList.length || !whatsappOperational) return;
    try {
      setIsSending(true);
      const conversation = conversationList.find((c) => c.clientReference === selectedClientReference);
      if (conversation) {
        const result = await sendImage(conversation.phoneNumber, file, caption);
        await applySendResult(result, true);
      }
    } finally {
      setIsSending(false);
    }
  };

  const handleSendAudio = async (blob: Blob, duration: string) => {
    if (channelTab === 'whatsapp_web') return;
    if (!selectedClientReference || !conversationList.length || !whatsappOperational) return;
    try {
      setIsSending(true);
      const conversation = conversationList.find((c) => c.clientReference === selectedClientReference);
      if (conversation) {
        const result = await sendAudio(conversation.phoneNumber, blob, duration);
        await applySendResult(result, true);
      }
    } finally {
      setIsSending(false);
    }
  };

  const handleSendLocation = async (latitude: number, longitude: number, name?: string) => {
    if (channelTab === 'whatsapp_web') return;
    if (!selectedClientReference || !conversationList.length || !whatsappOperational) return;
    try {
      setIsSending(true);
      const conversation = conversationList.find((c) => c.clientReference === selectedClientReference);
      if (conversation) {
        const result = await sendLocation(conversation.phoneNumber, latitude, longitude, name);
        await applySendResult(result, true);
      }
    } finally {
      setIsSending(false);
    }
  };

  const handleSendSticker = async (emoji: string) => {
    if (channelTab === 'whatsapp_web') return;
    if (!selectedClientReference || !conversationList.length || !whatsappOperational) return;
    try {
      setIsSending(true);
      const conversation = conversationList.find((c) => c.clientReference === selectedClientReference);
      if (conversation) {
        const result = await sendSticker(conversation.phoneNumber, new File([emoji], 'sticker.txt'));
        await applySendResult(result, true);
      }
    } finally {
      setIsSending(false);
    }
  };

  const handleSendTemplate = async () => {
    if (channelTab === 'whatsapp_web') return;
    if (!selectedClientReference || !conversationList.length || !templateName.trim()) return;
    const conversation = conversationList.find((c) => c.clientReference === selectedClientReference);
    if (!conversation) return;
    try {
      setIsSendingTemplate(true);
      const result = await sendTemplate(
        conversation.phoneNumber,
        templateName.trim(),
        templateLang.trim() || DEFAULT_TEMPLATE_LANG,
      );
      if (result.success) {
        setSendError(null);
        await loadMessages(1, 50);
      } else {
        setSendError(result.error ?? 'No se pudo enviar el template. Revisá nombre e idioma en Meta.');
      }
    } finally {
      setIsSendingTemplate(false);
    }
  };

  // ── Selección de conversación: resetea la vista al resumen ────────────────
  const handleSelectConversation = useCallback((clientReference: string) => {
    isNearBottomRef.current = true;
    pendingPrependRef.current = false;
    setHasUnreadBelow(false);
    setSelectedClientReference(clientReference);
    setChatView('summary');
    setIsSelectionMode(false);
    setSelectedMessageReferences([]);
    setConversations((prev) =>
      prev.map((c) =>
        c.clientReference === clientReference ? { ...c, unreadCount: 0 } : c,
      ),
    );
  }, []);

  const selectedConversation = conversationList.find(
    (c) => c.clientReference === selectedClientReference,
  );

  const isSupplierConversation =
    selectedConversation?.clientType === ClientType.SUPPLIER;

  const toggleMessageSelection = useCallback((messageReference: string) => {
    setSelectedMessageReferences((prev) =>
      prev.includes(messageReference)
        ? prev.filter((ref) => ref !== messageReference)
        : [...prev, messageReference],
    );
  }, []);

  const selectedMessageSet = useMemo(
    () => new Set(selectedMessageReferences),
    [selectedMessageReferences],
  );

  useLayoutEffect(() => {
    if (chatView !== 'chat') {
      return;
    }
    const container = messageScrollRef.current;
    if (!container) {
      return;
    }
    if (pendingPrependRef.current) {
      container.scrollTop += container.scrollHeight - previousScrollHeightRef.current;
      pendingPrependRef.current = false;
      return;
    }
    const latestMessageId = messages[messages.length - 1]?.messageId ?? null;
    const hasNewTail = latestMessageId !== lastSeenMessageIdRef.current;
    lastSeenMessageIdRef.current = latestMessageId;
    if (isNearBottomRef.current) {
      container.scrollTop = container.scrollHeight;
      setHasUnreadBelow(false);
      return;
    }
    if (hasNewTail) {
      setHasUnreadBelow(true);
    }
  }, [messages, chatView]);

  const handleMessageScroll = (): void => {
    const container = messageScrollRef.current;
    if (!container) {
      return;
    }
    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    const isNearBottom = distanceFromBottom < NEAR_BOTTOM_PX;
    isNearBottomRef.current = isNearBottom;
    if (isNearBottom) {
      setHasUnreadBelow(false);
    }
    if (
      container.scrollTop < 48 &&
      hasMoreMessages &&
      !isLoading &&
      !pendingPrependRef.current
    ) {
      previousScrollHeightRef.current = container.scrollHeight;
      pendingPrependRef.current = true;
      void loadMessages(messagesPage + 1, MESSAGE_PAGE_SIZE).then((loaded) => {
        if (!loaded) {
          pendingPrependRef.current = false;
        }
      });
    }
  };

  const jumpToLatest = (): void => {
    isNearBottomRef.current = true;
    setHasUnreadBelow(false);
    scrollToBottom();
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
        height: '100%',
      }}
    >
      {/* Banner de WhatsApp con inconvenientes */}
      {webLinkStatus === 'connecting' && (
        <Alert color="warning" variant="soft" sx={{ borderRadius: 0 }}>
          <Typography level="title-sm" sx={{ fontWeight: 'lg' }}>
            WhatsApp se está conectando
          </Typography>
        </Alert>
      )}
      {webLinkStatus === 'disconnected' && (
        <Alert color="danger" variant="soft" sx={{ borderRadius: 0 }}>
          <Typography level="title-sm" sx={{ fontWeight: 'lg' }}>
            WhatsApp está desconectado
          </Typography>
        </Alert>
      )}

      {showWhatsappBanner && (
        <Alert color="warning" variant="soft" sx={{ borderRadius: 0 }}>
          <Box>
            <Typography level="title-sm" sx={{ fontWeight: 'lg' }}>
              WhatsApp tiene inconvenientes
            </Typography>
            <Typography level="body-sm" sx={{ mt: 0.5 }}>
              {waCheckError
                ? 'No se pudo verificar el estado con el servidor. Revisá que la API esté en marcha.'
                : waStatus?.userMessage ??
                  'No podés enviar mensajes hasta que la conexión con Meta vuelva a funcionar.'}
            </Typography>
            {waStatus?.technicalDetail && (
              <Typography level="body-xs" sx={{ mt: 1, fontFamily: 'code' }}>
                {waStatus.technicalDetail}
              </Typography>
            )}
          </Box>
        </Alert>
      )}

      <Box
        sx={{
          display: 'flex',
          flex: 1,
          minHeight: 0,
          flexDirection: { xs: 'column', md: 'row' },
        }}
      >
        {/* ── Panel izquierdo: lista de conversaciones ── */}
        <Sheet
          variant="outlined"
          sx={{
            width: { xs: '100%', md: 320 },
            flexShrink: 0,
            borderRadius: 0,
            borderTop: 'none',
            borderBottom: { xs: '1px solid', md: 'none' },
            borderColor: 'divider',
            display: {
              xs: selectedClientReference ? 'none' : 'flex',
              md: 'flex',
            },
            flexDirection: 'column',
            minHeight: 0,
            flex: { xs: 1, md: 'none' },
          }}
        >
          <Tabs
            value={channelTab}
            onChange={(_, value) => setChannelTab(value as ChatChannelProvider)}
            sx={{ borderBottom: '1px solid', borderColor: 'divider', px: 0.5, pt: 0.5 }}
          >
            <TabList tabFlex={1}>
              <Tab value="meta">Meta</Tab>
              <Tab value="whatsapp_web">WhatsApp Web</Tab>
            </TabList>
          </Tabs>
          <ConversationList
            conversations={conversationList}
            selectedClientReference={selectedClientReference}
            onSelectConversation={handleSelectConversation}
            isLoading={isLoadingConversations}
          />
        </Sheet>

        {/* ── Panel derecho: chat ── */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            minHeight: 0,
            display: {
              xs: selectedClientReference ? 'flex' : 'none',
              md: 'flex',
            },
            flexDirection: 'column',
            bgcolor: 'background.surface',
          }}
        >
          {selectedConversation ? (
            <>
              {/* Header: siempre visible con nombre y teléfono */}
              <ChatHeader
                clientReference={selectedConversation.clientReference}
                clientName={selectedConversation.clientName}
                phoneNumber={selectedConversation.phoneNumber}
                isSocketConnected={isConnected}
                isWhatsappOperational={channelTab === 'meta' ? whatsappOperational : true}
                channel={channelTab}
                whatsappLinkStatus={webLinkStatus}
                clientType={selectedConversation.clientType}
                onBack={() => setSelectedClientReference(null)}
                onUpdateClientType={async (clientReference, clientType) => {
                  try {
                    await clientsApi.update(clientReference, { clientType });
                    setConversations((prev) =>
                      prev.map((c) =>
                        c.clientReference === clientReference ? { ...c, clientType } : c,
                      ),
                    );
                  } catch (error) {
                    console.error('Error updating client type:', error);
                    setSendError('No se pudo actualizar el tipo de cliente.');
                  }
                }}
              />

              {/* Banner: solo lectura WhatsApp Web */}
              {channelTab === 'whatsapp_web' && (
                <Alert color="neutral" variant="soft" sx={{ borderRadius: 0 }}>
                  <Typography level="body-sm">
                    Solo lectura: esta vista muestra mensajes capturados por WhatsApp Web. No
                    podés enviar ni editar desde aquí.
                  </Typography>
                </Alert>
              )}

              {/* Banner: ventana de 24hs expirada */}
              {channelTab === 'meta' && failedEvent && failedEvent.isWindowExpired && (
                <Alert
                  color="danger"
                  variant="soft"
                  startDecorator={<WarningAmberIcon />}
                  endDecorator={
                    <IconButton variant="plain" size="sm" color="danger" onClick={dismissFailedEvent}>
                      <CloseIcon />
                    </IconButton>
                  }
                  sx={{ borderRadius: 0, mx: 0 }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography level="title-sm" color="danger">
                      Mensaje no entregado
                    </Typography>
                    <Typography level="body-sm" sx={{ mt: 0.5 }}>
                      Pasaron más de 24 hs desde la última respuesta del cliente.
                      Enviá un template aprobado en Meta para retomar la conversación.
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 1.5, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                      <Input
                        size="sm"
                        placeholder="Nombre del template"
                        value={templateName}
                        onChange={(e) => setTemplateName(e.target.value)}
                        sx={{ flex: 1, minWidth: 140 }}
                      />
                      <Input
                        size="sm"
                        placeholder="Idioma (ej: es_AR)"
                        value={templateLang}
                        onChange={(e) => setTemplateLang(e.target.value)}
                        sx={{ width: 120 }}
                      />
                      <Button
                        size="sm"
                        color="primary"
                        loading={isSendingTemplate}
                        disabled={!templateName.trim()}
                        startDecorator={<SendIcon />}
                        onClick={handleSendTemplate}
                      >
                        Enviar template
                      </Button>
                    </Box>
                  </Box>
                </Alert>
              )}

              {/* ── Tabs de vista: Resumen / Conversación ── */}
              <Tabs
                value={chatView}
                onChange={(_, v) => {
                  const nextView = v as 'summary' | 'chat';
                  if (nextView === 'chat') {
                    isNearBottomRef.current = true;
                  }
                  setChatView(nextView);
                }}
                sx={{ borderBottom: '1px solid', borderColor: 'divider' }}
              >
                <TabList>
                  <Tab value="summary">
                    <DescriptionOutlinedIcon sx={{ fontSize: 16, mr: 0.5 }} />
                    Resumen del pedido
                  </Tab>
                  <Tab value="chat">
                    <ChatBubbleOutlineIcon sx={{ fontSize: 16, mr: 0.5 }} />
                    Conversación
                  </Tab>
                </TabList>
              </Tabs>

              {/* ── Vista: Resumen del pedido ── */}
              {chatView === 'summary' && (
                <OrderSummaryPanel
                  clientReference={selectedConversation.clientReference}
                  messages={messages}
                />
              )}

              {/* ── Vista: Conversación completa ── */}
              {chatView === 'chat' && isSupplierConversation ? (
                <Box
                  sx={{
                    px: 2,
                    py: 1,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    display: 'flex',
                    gap: 1,
                    flexWrap: 'wrap',
                    alignItems: 'center',
                  }}
                >
                  <Button
                    size="sm"
                    variant={isSelectionMode ? 'solid' : 'outlined'}
                    onClick={() => {
                      setIsSelectionMode((prev) => !prev);
                      setSelectedMessageReferences([]);
                    }}
                  >
                    {isSelectionMode ? 'Cancelar selección' : 'Seleccionar mensajes'}
                  </Button>
                  {isSelectionMode && selectedMessageReferences.length > 0 ? (
                    <Button
                      size="sm"
                      color="primary"
                      startDecorator={<Inventory2OutlinedIcon />}
                      onClick={() => setImportModalOpen(true)}
                    >
                      Cargar como productos ({selectedMessageReferences.length})
                    </Button>
                  ) : null}
                </Box>
              ) : null}

              {chatView === 'chat' && (
                <Box sx={{ flex: 1, minHeight: 0, position: 'relative', display: 'flex' }}>
                <Box
                  ref={messageScrollRef}
                  onScroll={handleMessageScroll}
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    p: 2,
                    bgcolor: 'background.level1',
                  }}
                >
                  {isLoading && messages.length === 0 ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 200 }}>
                      <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                        Cargando mensajes...
                      </Typography>
                    </Box>
                  ) : messages.length === 0 ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 200 }}>
                      <Typography level="body-sm" sx={{ color: 'neutral.500' }}>
                        Sin mensajes aún. Inicia la conversación
                      </Typography>
                    </Box>
                  ) : (
                    <>
                      {messages.map((msg, index) => {
                        const previous = index > 0 ? messages[index - 1] : null;
                        const showDay =
                          previous == null ||
                          startOfLocalDay(new Date(msg.timestamp)) !==
                            startOfLocalDay(new Date(previous.timestamp));
                        return (
                          <React.Fragment key={msg.messageId}>
                            {showDay ? (
                              <Typography
                                level="body-xs"
                                sx={{
                                  textAlign: 'center',
                                  color: 'neutral.500',
                                  my: 1.5,
                                }}
                              >
                                {formatDaySeparator(new Date(msg.timestamp))}
                              </Typography>
                            ) : null}
                            <MessageBubble
                              message={msg}
                              isOwn={Boolean(msg.fromMe)}
                              isSelectable={isSelectionMode && isSupplierConversation}
                              isSelected={selectedMessageSet.has(msg.reference)}
                              onToggleSelect={toggleMessageSelection}
                            />
                          </React.Fragment>
                        );
                      })}
                      <div ref={messagesEndRef} />
                    </>
                  )}
                </Box>
                {hasUnreadBelow ? (
                  <Button
                    size="sm"
                    variant="soft"
                    onClick={jumpToLatest}
                    sx={{
                      position: 'absolute',
                      bottom: 12,
                      left: '50%',
                      transform: 'translateX(-50%)',
                    }}
                  >
                    Mensaje nuevo
                  </Button>
                ) : null}
                </Box>
              )}

              {/* ComposerBar: siempre visible */}
              <ComposerBar
                onSendText={handleSendText}
                onSendImage={handleSendImage}
                onSendAudio={handleSendAudio}
                onSendLocation={handleSendLocation}
                onSendSticker={handleSendSticker}
                isLoading={isSending}
                whatsappAvailable={channelTab === 'meta' ? whatsappOperational : true}
                channel={channelTab}
              />
            </>
          ) : (
            <Box
              sx={{
                flex: 1,
                display: { xs: 'none', md: 'flex' },
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography level="body-md" sx={{ color: 'neutral.500' }}>
                Seleccioná una conversación
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      <Snackbar
        variant="soft"
        color="danger"
        open={Boolean(sendError)}
        onClose={() => setSendError(null)}
        autoHideDuration={9000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ maxWidth: 480 }}
      >
        <Typography level="body-sm">{sendError}</Typography>
      </Snackbar>

      {selectedConversation && isSupplierConversation ? (
        <SupplierProductImportModal
          open={importModalOpen}
          supplierClientReference={selectedConversation.clientReference}
          messageReferences={selectedMessageReferences}
          onClose={() => setImportModalOpen(false)}
          onSuccess={() => {
            setSelectedMessageReferences([]);
            setIsSelectionMode(false);
          }}
        />
      ) : null}
    </Box>
  );
};

export default ChatPage;
